import React from 'react';
import { formatAddedByLabel } from '../../utils/queueAttribution';
import { isEventHost } from '../../utils/identity';

function formatEndedAt(endedAt) {
  const ms = Number(endedAt);
  if (!Number.isFinite(ms) || ms <= 0) return null;
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return null;
  }
}

/**
 * Read-only recap of an ended party. No playback, queue writes, settings, or
 * rejoin controls.
 */
export default function EndedSessionView({
  eventData,
  queue = [],
  userId,
  liveJustEnded = false,
  onHome,
}) {
  const title = eventData?.title || 'This party';
  const endedAt = formatEndedAt(eventData?.endedAt);
  const participants = Array.isArray(eventData?.participants) ? eventData.participants : [];
  const roleLabel = isEventHost(userId, eventData?.hostId) ? 'Host' : 'Guest';

  return (
    <div className="rhythm-page flex flex-col items-center bg-gradient-to-b from-gray-900 to-black p-4 text-white">
      <div className="rhythm-page-shell rhythm-page-shell--join w-full space-y-5 py-8">
        {liveJustEnded ? (
          <header className="text-center space-y-2">
            <h1 className="text-3xl font-bold">Party ended</h1>
            <p className="text-gray-400">
              {title} has ended. Guests can no longer join or add songs.
            </p>
          </header>
        ) : (
          <header className="text-center space-y-2">
            <p className="text-xs uppercase tracking-widest text-gray-500">Previous session</p>
            <h1 className="text-3xl font-bold">{title}</h1>
          </header>
        )}

        <dl className="space-y-2 rounded-xl bg-white/5 p-4 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-400">Status</dt>
            <dd>Ended</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-400">Event code</dt>
            <dd className="font-mono">{eventData?.id}</dd>
          </div>
          {endedAt && (
            <div className="flex justify-between gap-3">
              <dt className="text-gray-400">Ended at</dt>
              <dd>{endedAt}</dd>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <dt className="text-gray-400">Your role</dt>
            <dd>{roleLabel}</dd>
          </div>
        </dl>

        <section className="space-y-2">
          <h2 className="text-sm font-medium text-gray-300">Participants</h2>
          {participants.length === 0 ? (
            <p className="text-sm text-gray-500">No participant list was saved.</p>
          ) : (
            <ul className="space-y-1">
              {participants.map((participant) => (
                <li
                  key={participant.id}
                  className="flex justify-between rounded-lg bg-white/5 px-3 py-2 text-sm"
                >
                  <span>{participant.name || 'Guest'}</span>
                  <span className="text-gray-400">
                    {participant.role === 'host' ? 'Host' : 'Guest'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-medium text-gray-300">Final queue</h2>
          {queue.length === 0 ? (
            <p className="text-sm text-gray-500">No songs were kept for this session.</p>
          ) : (
            <ol className="space-y-1">
              {queue.map((song, index) => (
                <li
                  key={song.queueItemId || `${song.id}-${index}`}
                  className="rounded-lg bg-white/5 px-3 py-2 text-sm"
                >
                  <p className="truncate text-white">{index + 1}. {song.title}</p>
                  <p className="truncate text-xs text-gray-400">
                    {formatAddedByLabel(song.addedBy, participants)}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </section>

        <button
          type="button"
          onClick={onHome}
          className="w-full rounded-lg bg-white/10 px-4 py-2 text-sm hover:bg-white/20 transition-colors"
        >
          Go to Rhythmé home
        </button>
      </div>
    </div>
  );
}
